import { eventBus } from './eventBus.js'
import { SerializerFactory } from '../serializers/serializerFactory.js'
import { cacheManager } from '../storage/cacheManager.js'
import EXTERNAL_SERVICES_CONFIG from '@/shared/configs/externalServicesConfig.js'

export class BatchRequestQueue {
  constructor() {
    this.pendingRequests = []
    this.batchInterval = 1000
    this.maxBatchSize = 80
    this.timer = null
    this.processing = false
    this.excludedUrlBases = []
  }

  setup(delay = 1000, maxSize = 80, excludedUrlBases = []) {
    this.batchInterval = delay
    this.maxBatchSize = maxSize
    this.excludedUrlBases = excludedUrlBases
  }

  // Vérifie si une URL doit être exclue du batch
  isUrlExcluded(url) {
    return this.excludedUrlBases.some(base => url.startsWith(base))
  }

  // Extrait la base de l'URL pour grouper les requêtes similaires
  getUrlBase(url) {
    try {
      const urlObj = new URL(url)
      const pathParts = urlObj.pathname.split('/').filter(p => p)

      // Enlever le dernier segment (souvent un ID)
      if (pathParts.length > 1) {
        pathParts.pop()
        urlObj.pathname = pathParts.join('/')
      }

      return urlObj.origin + urlObj.pathname
    } catch {
      // Si l'URL n'est pas valide, retourner l'URL telle quelle
      const parts = url.split('/')
      if (parts.length > 1) {
        parts.pop()
        return parts.join('/')
      }
      return url
    }
  }

  schedule(requestMeta) {
    return new Promise((resolve, reject) => {
      const { requestId, method, url, options, entityType, payload } = requestMeta

      this.pendingRequests.push({
        requestId,
        method,
        url,
        options,
        entityType,
        payload,
        resolve,
        reject,
        timestamp: Date.now(),
      })

      if (this.pendingRequests.length >= this.maxBatchSize) {
        this.processBatch()
      } else if (!this.timer) {
        this.scheduleBatch()
      }
    })
  }

  scheduleBatch() {
    if (this.timer) {
      clearTimeout(this.timer)
    }

    this.timer = setTimeout(() => {
      this.processBatch()
      this.timer = null
    }, this.batchInterval)
  }

  async processBatch() {
    if (this.processing || this.pendingRequests.length === 0) return

    this.processing = true

    const requests = this.pendingRequests.splice(0)
    this.pendingRequests.length = 0

    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }

    try {
      // Séparer GET et autres
      const getRequests = []
      const otherRequests = []

      requests.forEach((req) => {
        if (req.method === 'GET') {
          getRequests.push(req)
        } else {
          otherRequests.push(req)
        }
      })

      // Traiter les GET avec cache
      const batchGetRequests = []
      const excludedGetRequests = []

      for (const req of getRequests) {
        // Vérifier si l'URL est exclue du batch
        if (this.isUrlExcluded(req.url)) {
          excludedGetRequests.push(req)
          continue
        }

        const cached = await cacheManager.get(req.method, req.url)
        if (cached) {
          eventBus.emit(`RESPONSE:${req.requestId}`, {
            statusCode: 200,
            message: 'Loaded from cache',
            data: cached,
          })
          req.resolve({ data: cached })
        } else {
          batchGetRequests.push(req)
        }
      }

      // Traiter les GET exclues avec déduplication des fetchs, mais séparation des serializers
      const excludedGetMap = new Map()

      // Grouper par URL
      for (const req of excludedGetRequests) {
        if (!excludedGetMap.has(req.url)) {
          excludedGetMap.set(req.url, [])
        }
        excludedGetMap.get(req.url).push(req)
      }

      // Pour chaque URL unique, faire un fetch puis appliquer les serializers individuellement
      const excludedGetPromises = Array.from(excludedGetMap.entries()).map(([url, requests]) => {
        return (async () => {
          try {
            // Un seul fetch pour cette URL
            const response = await fetch(url, {
              method: 'GET',
              headers: requests[0].options.headers,
            })

            const rawData = await response.json()

            // Appliquer chaque serializer individuellement sur les mêmes données brutes
            for (const req of requests) {
              try {
                const serializer = SerializerFactory.get(req.entityType)
                const deserialized = serializer.deserialize(rawData, req.payload, req.method)
                const finalData = (deserialized instanceof Promise ? await deserialized : deserialized) || {}
                finalData.raw = rawData

                eventBus.emit(`RESPONSE:${req.requestId}`, {
                  statusCode: 200,
                  message: 'Successful request',
                  data: finalData,
                })

                req.resolve({ data: finalData })
              } catch (error) {
                console.error(`Serializer error for excluded request ${req.requestId}:`, error)
                eventBus.emit(`RESPONSE:${req.requestId}`, {
                  statusCode: 500,
                  message: error.message || 'Serializer error',
                  error: error.message,
                })

                req.resolve({ data: { statusCode: 500, message: error.message || 'Serializer error', data: null } })
              }
            }
          } catch (error) {
            // En cas d'erreur de fetch, résoudre avec l'erreur au lieu de rejeter
            console.error(`Fetch error for excluded URL ${url}:`, error)
            for (const req of requests) {
              eventBus.emit(`RESPONSE:${req.requestId}`, {
                statusCode: 500,
                message: error.message || 'Fetch error',
                error: error.message,
              })

              req.resolve({ data: { statusCode: 500, message: error.message || 'Fetch error', data: null } })
            }
          }
        })()
      })

      // Traiter les requêtes non-GET immédiatement en parallèle
      const otherPromises = otherRequests.map(async (req) => {
        try {
          const response = await fetch(req.url, {
            method: req.method,
            headers: req.options.headers,
            body: req.options.body,
          })

          const data = await response.json()

          try {
            const serializer = SerializerFactory.get(req.entityType)
            const deserialized = serializer.deserialize(data, req.payload, req.method)
            const finalData = (deserialized instanceof Promise ? await deserialized : deserialized) || {}
            finalData.raw = data

            eventBus.emit(`RESPONSE:${req.requestId}`, {
              statusCode: 200,
              message: 'Successful request',
              data: finalData,
            })

            req.resolve({ data: finalData })
          } catch (error) {
            console.error(`Serializer error for non-GET request ${req.requestId}:`, error)
            eventBus.emit(`RESPONSE:${req.requestId}`, {
              statusCode: 500,
              message: error.message || 'Serializer error',
              error: error.message,
            })

            req.resolve({ data: { statusCode: 500, message: error.message || 'Serializer error', data: null } })
          }
        } catch (error) {
          console.error(`Request error for ${req.requestId}:`, error)
          eventBus.emit(`RESPONSE:${req.requestId}`, {
            statusCode: 500,
            message: error.message || 'Request error',
            error: error.message,
          })

          req.resolve({ data: { statusCode: 500, message: error.message || 'Request error', data: null } })
        }
      })

      // Traiter les GET en batch HTTP, groupés par base d'URL
      if (batchGetRequests.length > 0) {
        // Grouper les requêtes par base d'URL
        const groupedRequests = new Map()
        for (const req of batchGetRequests) {
          const urlBase = this.getUrlBase(req.url)
          if (!groupedRequests.has(urlBase)) {
            groupedRequests.set(urlBase, [])
          }
          groupedRequests.get(urlBase).push(req)
        }

        // Envoyer un batch par groupe
        const batchPromises = Array.from(groupedRequests.entries()).map(([urlBase, requests]) => {
          return this.sendBatch(requests)
        })

        await Promise.allSettled(batchPromises)
      }

      // Attendre que toutes les requêtes non-GET et GET exclues soient terminées
      await Promise.allSettled([...otherPromises, ...excludedGetPromises])

    } finally {
      this.processing = false

      if (this.pendingRequests.length > 0) {
        this.scheduleBatch()
      }
    }
  }

  async sendBatch(requests) {
    if (requests.length === 0) return

    const batchPayload = {
      requests: requests.map((req) => ({
        id: req.requestId,
        url: req.url,
        method: req.method,
        headers: req.options.headers,
      })),
      options: {
        parallel: true,
      },
    }

    try {
      const batchUrl = `${EXTERNAL_SERVICES_CONFIG.DATA_STORE_HOST}/data-store-partial-saver/batch`
      const batchResponse = await fetch(batchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(batchPayload),
      })

      const batchResult = await batchResponse.json()

      if (batchResult.success && batchResult.responses) {
        for (const resp of batchResult.responses) {
          const req = requests.find((r) => r.requestId === resp.id)
          if (!req) continue

          if (resp.success) {
            try {
              const data = resp.data
              const serializer = SerializerFactory.get(req.entityType)
              const deserialized = serializer.deserialize(data, req.payload, req.method)
              const finalData = (deserialized instanceof Promise ? await deserialized : deserialized) || {}
              finalData.raw = data

              cacheManager.set(req.method, req.url, finalData)

              eventBus.emit(`RESPONSE:${req.requestId}`, {
                statusCode: 200,
                message: 'Successful request',
                data: finalData,
              })

              req.resolve({ data: finalData })
            } catch (error) {
              console.error(`Serializer error for request ${req.requestId}:`, error)
              eventBus.emit(`RESPONSE:${req.requestId}`, {
                statusCode: 500,
                message: error.message || 'Serializer error',
                error: error.message,
              })

              req.resolve({ data: { statusCode: 500, message: error.message || 'Serializer error', data: null } })
            }
          } else {
            const errorMessage = resp.message || 'Request failed'
            console.error(`Request ${req.requestId} failed:`, errorMessage)
            eventBus.emit(`RESPONSE:${req.requestId}`, {
              statusCode: resp.statusCode || 500,
              message: errorMessage,
              error: resp.error,
            })

            req.resolve({ data: { statusCode: resp.statusCode || 500, message: errorMessage, data: null } })
          }
        }
      } else {
        // Échec global du batch
        const errorMessage = batchResult.message || 'Batch request failed'
        console.error('Batch request failed:', errorMessage)
        for (const req of requests) {
          eventBus.emit(`RESPONSE:${req.requestId}`, {
            statusCode: 500,
            message: errorMessage,
            error: batchResult.message,
          })

          req.resolve({ data: { statusCode: 500, message: errorMessage, data: null } })
        }
      }
    } catch (error) {
      console.error('Batch fetch error:', error)
      for (const req of requests) {
        eventBus.emit(`RESPONSE:${req.requestId}`, {
          statusCode: 500,
          message: error.message || 'Batch fetch error',
          error: error.message,
        })

        req.resolve({ data: { statusCode: 500, message: error.message || 'Batch fetch error', data: null } })
      }
    }
  }
}
